/**
 * Customer Rating Distribution View (CR-6381)
 *
 * Shows the distribution of supplier ratings for customer users.
 * Displays Monthly and Overall (YTD) charts with clickable bars
 * for drill-down to individual suppliers.
 */
import { useState, useEffect, useCallback } from 'react'
import { Card, CardContent } from '../ui/card'
import { Button } from '../ui/button'
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select'
import { Label } from '../ui/label'
import { Loader2, RefreshCw, BarChart3, Filter } from 'lucide-react'
import { PerformanceRatingDistribution } from './PerformanceRatingDistribution'
import { RatingDrilldownModal } from './RatingDrilldownModal'
import { ChangeRequestDrilldownModal } from './ChangeRequestDrilldownModal'
import { type SupplierOption } from './performanceConstants'
import {
  supplierRatingDistributionAPI,
  changeRequestsMonthlyAPI,
  coreAPI,
  type RatingDistributionData,
  type ChangeRequestsMonthlyData,
} from '../../lib/api'
import { ChangeRequestsMonthlyCharts } from './ChangeRequestsMonthlyCharts'

/**
 * Get default date range for the Rating Distribution view.
 * Matches the old supplier_portal default behavior:
 * - Start: 1st day of current month, previous year (e.g., Aug 1, 2025)
 * - End: Last day of current month, current year (e.g., Aug 31, 2026)
 */
function getDefaultDateRange() {
  const now = new Date()
  const currentMonth = now.getMonth()
  const currentYear = now.getFullYear()

  // Start date: 1st of current month, previous year
  const startYear = currentYear - 1
  const startMonth = String(currentMonth + 1).padStart(2, '0')

  // End date: Last day of current month, current year
  const lastDayOfMonth = new Date(currentYear, currentMonth + 1, 0).getDate()
  const endMonth = String(currentMonth + 1).padStart(2, '0')

  return {
    startDate: `${startYear}-${startMonth}-01`,
    endDate: `${currentYear}-${endMonth}-${String(lastDayOfMonth).padStart(2, '0')}`,
  }
}

export function CustomerRatingDistributionView() {
  const [distribution, setDistribution] = useState<RatingDistributionData | null>(null)
  const [changeRequests, setChangeRequests] = useState<ChangeRequestsMonthlyData | null>(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [showFilters, setShowFilters] = useState(false)

  // Supplier list for dropdown
  const [suppliers, setSuppliers] = useState<SupplierOption[]>([])
  const [loadingSuppliers, setLoadingSuppliers] = useState(false)

  // Filter state - use current month for real-time dashboard view
  const defaultDates = getDefaultDateRange()
  const [selectedSupplier, setSelectedSupplier] = useState<string>('all')
  const [startDate, setStartDate] = useState(defaultDates.startDate)
  const [endDate, setEndDate] = useState(defaultDates.endDate)
  const [strategicOnly, setStrategicOnly] = useState(false)
  const [excludeNA, setExcludeNA] = useState(false)

  // Applied filters (only update on Search click)
  const [appliedFilters, setAppliedFilters] = useState({
    supplier: 'all',
    startDate: defaultDates.startDate,
    endDate: defaultDates.endDate,
    strategicOnly: false,
  })

  // Rating drilldown modal state
  const [drilldownOpen, setDrilldownOpen] = useState(false)
  const [drilldownRating, setDrilldownRating] = useState<string>('')
  const [drilldownPeriod, setDrilldownPeriod] = useState<'monthly' | 'overall'>('monthly')

  // Change request drilldown modal state
  const [crDrilldownOpen, setCrDrilldownOpen] = useState(false)
  const [crDrilldownMonth, setCrDrilldownMonth] = useState<string>('')
  const [crDrilldownType, setCrDrilldownType] = useState<'date' | 'price' | 'qty'>('date')
  const [crDrilldownInitiator, setCrDrilldownInitiator] = useState<'customer' | 'supplier' | undefined>()

  // Load suppliers for filter
  useEffect(() => {
    const loadSuppliers = async () => {
      setLoadingSuppliers(true)
      try {
        const params = strategicOnly ? { strategic_only: true } : undefined
        const response = await coreAPI.getSupplierSitesForFilter(params)
        const list = (response.data.results || [])
          .filter((s) => s.id && s.name)
          .map((s) => ({ id: s.id, name: s.name }))
        setSuppliers(list)
      } catch (err) {
        console.error('[CustomerRatingDistributionView] Error loading suppliers:', err)
      } finally {
        setLoadingSuppliers(false)
      }
    }
    loadSuppliers()
  }, [strategicOnly])

  // Fetch distribution and change requests data
  const fetchData = useCallback(async () => {
    try {
      setLoading(true)
      setError(null)

      const params: Record<string, string | boolean | number> = {}
      if (appliedFilters.supplier !== 'all') {
        params['supplier_id'] = parseInt(appliedFilters.supplier, 10)
      }
      if (appliedFilters.startDate) {
        params['start_date'] = appliedFilters.startDate
      }
      if (appliedFilters.endDate) {
        params['end_date'] = appliedFilters.endDate
      }
      if (appliedFilters.strategicOnly) {
        params['strategic_only'] = true
      }

      // Fetch both APIs in parallel
      const [distributionRes, changeRequestsRes] = await Promise.all([
        supplierRatingDistributionAPI.get(params),
        changeRequestsMonthlyAPI.get(params),
      ])

      setDistribution(distributionRes.data)
      setChangeRequests(changeRequestsRes.data)
    } catch (err: unknown) {
      const axiosError = err as { response?: { status?: number }; message?: string }
      console.error('[CustomerRatingDistributionView] Error fetching data:', axiosError.message)
      setError('Failed to load rating distribution. Please try again.')
      setDistribution(null)
      setChangeRequests(null)
    } finally {
      setLoading(false)
    }
  }, [appliedFilters])

  useEffect(() => {
    fetchData()
  }, [fetchData])

  // Handle search button click
  const handleSearch = useCallback(() => {
    setAppliedFilters({
      supplier: selectedSupplier,
      startDate,
      endDate,
      strategicOnly,
    })
  }, [selectedSupplier, startDate, endDate, strategicOnly])

  // Handle reset button click
  const handleReset = useCallback(() => {
    const defaults = getDefaultDateRange()
    setSelectedSupplier('all')
    setStartDate(defaults.startDate)
    setEndDate(defaults.endDate)
    setStrategicOnly(false)
    setExcludeNA(false)
    setAppliedFilters({
      supplier: 'all',
      startDate: defaults.startDate,
      endDate: defaults.endDate,
      strategicOnly: false,
    })
  }, [])

  // Handle rating bar click
  const handleMonthlyBarClick = useCallback((rating: string) => {
    setDrilldownRating(rating)
    setDrilldownPeriod('monthly')
    setDrilldownOpen(true)
  }, [])

  const handleOverallBarClick = useCallback((rating: string) => {
    setDrilldownRating(rating)
    setDrilldownPeriod('overall')
    setDrilldownOpen(true)
  }, [])

  // Handle change request bar click
  const handleChangeRequestBarClick = useCallback(
    (month: string, changeType: 'date' | 'price' | 'qty', initiator?: 'customer' | 'supplier') => {
      setCrDrilldownMonth(month)
      setCrDrilldownType(changeType)
      setCrDrilldownInitiator(initiator)
      setCrDrilldownOpen(true)
    },
    []
  )

  // Filter out N/A if toggle is on
  const filterNA = (data: Record<string, number>): Record<string, number> => {
    if (!excludeNA) return data
    const filtered = { ...data }
    delete filtered['N/A']
    return filtered
  }

  return (
    <div className="space-y-4">
      {/* Header */}
      <div className="bg-card rounded-lg shadow-sm p-4">
        <div className="flex justify-between items-center">
          <div>
            <h2 className="text-lg font-semibold">Supplier Performance Ratings</h2>
            <p className="text-sm text-muted-foreground">
              View the distribution of supplier ratings across your supply base
            </p>
          </div>
          <div className="flex gap-2">
            <Button
              variant={showFilters ? 'default' : 'outline'}
              size="sm"
              onClick={() => setShowFilters(!showFilters)}
            >
              <Filter className="h-4 w-4 mr-2" />
              Filter
            </Button>
          </div>
        </div>
      </div>

      {/* Filter Panel */}
      {showFilters && (
        <Card className="shadow-sm">
          <CardContent className="py-4">
            <div className="flex items-end gap-4">
              {/* Supplier Dropdown */}
              <div className="flex flex-col gap-1">
                <Label htmlFor="supplier" className="text-sm">Supplier</Label>
                <Select
                  value={selectedSupplier}
                  onValueChange={setSelectedSupplier}
                  disabled={loadingSuppliers}
                >
                  <SelectTrigger id="supplier" className="w-[210px] h-9">
                    {loadingSuppliers ? (
                      <Loader2 className="h-4 w-4 animate-spin" />
                    ) : (
                      <SelectValue placeholder="All Suppliers" />
                    )}
                  </SelectTrigger>
                  <SelectContent className="max-w-[300px]">
                    <SelectItem value="all">All Suppliers</SelectItem>
                    {suppliers.map((supplier) => (
                      <SelectItem key={supplier.id} value={String(supplier.id)}>
                        <span className="truncate block max-w-[240px]">{supplier.name}</span>
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              {/* From Date */}
              <div className="flex flex-col gap-1">
                <Label htmlFor="startDate" className="text-sm">From Date</Label>
                <input
                  id="startDate"
                  type="date"
                  value={startDate}
                  onChange={(e) => setStartDate(e.target.value)}
                  className="w-[160px] h-9 px-2 border border-input rounded-md text-sm bg-background"
                />
              </div>

              {/* To Date */}
              <div className="flex flex-col gap-1">
                <Label htmlFor="endDate" className="text-sm">To Date</Label>
                <input
                  id="endDate"
                  type="date"
                  value={endDate}
                  onChange={(e) => setEndDate(e.target.value)}
                  className="w-[160px] h-9 px-2 border border-input rounded-md text-sm bg-background"
                />
              </div>

              {/* Checkboxes */}
              <div className="flex flex-col gap-1 justify-center pb-0.5">
                <label className="flex items-center gap-2 text-sm cursor-pointer whitespace-nowrap">
                  <input
                    type="checkbox"
                    checked={strategicOnly}
                    onChange={(e) => setStrategicOnly(e.target.checked)}
                    className="rounded"
                  />
                  Strategic Suppliers Only
                </label>
                <label className="flex items-center gap-2 text-sm cursor-pointer whitespace-nowrap">
                  <input
                    type="checkbox"
                    checked={excludeNA}
                    onChange={(e) => setExcludeNA(e.target.checked)}
                    className="rounded"
                  />
                  Exclude N/A Ratings
                </label>
              </div>

              {/* Action Buttons */}
              <div className="flex gap-2">
                <Button onClick={handleSearch} className="h-9 px-5">
                  Search
                </Button>
                <Button variant="outline" onClick={handleReset} className="h-9 px-5">
                  Reset
                </Button>
              </div>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Loading State */}
      {loading && (
        <div className="flex items-center justify-center py-12">
          <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
        </div>
      )}

      {/* Error State */}
      {!loading && error && (
        <Card className="shadow-sm">
          <CardContent className="pt-6">
            <div className="text-center py-12">
              <div className="text-red-500 mb-4">{error}</div>
              <Button variant="outline" onClick={fetchData}>
                <RefreshCw className="h-4 w-4 mr-2" />
                Retry
              </Button>
            </div>
          </CardContent>
        </Card>
      )}

      {/* No Data State */}
      {!loading && !error && !distribution && (
        <Card className="shadow-sm">
          <CardContent className="pt-6">
            <div className="text-center py-12">
              <BarChart3 className="h-12 w-12 mx-auto text-muted-foreground mb-4" />
              <h3 className="text-lg font-medium mb-2">No Rating Data</h3>
              <p className="text-sm text-muted-foreground">
                Rating distribution will appear here once data is available.
              </p>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Distribution Charts */}
      {!loading && !error && distribution && (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <PerformanceRatingDistribution
            data={filterNA(distribution.monthly)}
            title={`Monthly Supplier Ratings - ${distribution.period.month}`}
            onBarClick={handleMonthlyBarClick}
          />
          <PerformanceRatingDistribution
            data={filterNA(distribution.overall)}
            title={`Overall Supplier Ratings - YTD ${distribution.period.year}`}
            onBarClick={handleOverallBarClick}
          />
        </div>
      )}

      {/* Change Requests Charts */}
      {!loading && !error && changeRequests && (
        <ChangeRequestsMonthlyCharts
          data={changeRequests}
          onBarClick={handleChangeRequestBarClick}
        />
      )}

      {/* Info Banner */}
      <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
        <p className="text-sm text-blue-800">
          <strong>Note:</strong> Supplier ratings are calculated based on OTIF (On-Time In-Full) delivery performance.
          Click on any rating bar to see which suppliers have that rating.
        </p>
      </div>

      {/* Rating Drilldown Modal */}
      <RatingDrilldownModal
        open={drilldownOpen}
        onClose={() => setDrilldownOpen(false)}
        rating={drilldownRating}
        period={drilldownPeriod}
        startDate={appliedFilters.startDate}
        endDate={appliedFilters.endDate}
        strategicOnly={appliedFilters.strategicOnly}
      />

      {/* Change Request Drilldown Modal */}
      <ChangeRequestDrilldownModal
        open={crDrilldownOpen}
        onClose={() => setCrDrilldownOpen(false)}
        month={crDrilldownMonth}
        changeType={crDrilldownType}
        initiator={crDrilldownInitiator}
        supplierId={appliedFilters.supplier !== 'all' ? parseInt(appliedFilters.supplier, 10) : undefined}
        strategicOnly={appliedFilters.strategicOnly}
      />
    </div>
  )
}
